import { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { useReferences } from '../../../../../helpers/context_manager/useReferences';
import { toOptions } from '../../../../../helpers/toOptions';

const p = (path: string) => `transport.${path}` as const;

export const TransportForm: FC = () => {
  const { register, watch, setValue } = useFormContext<RequestsFormValues>();
  const { references, loading } = useReferences();

  const departmentOptions = useMemo(
    () =>
      toOptions(
        references.providerDepartments,
        undefined,
        (d) => `${d.icp} – ${d.name || d.organization_name}`
      ),
    [references.providerDepartments]
  );

  const useProviderDept = watch(p('form_data.origin.use_provider_department') as any);
  const examinationTypeId = watch('examinationType');
  const examTypes = references.examinationTypes ?? [];
  const selectedExamType = examTypes.find((t) => String(t.id) === String(examinationTypeId));
  const examName = selectedExamType?.name?.toLowerCase() ?? '';
  const examCode = selectedExamType?.code?.toLowerCase() ?? '';
  const isSudicial = examName.includes('soudní') || examCode.includes('soudni');
  const isMedical = examName.includes('zdravotní') || examCode.includes('zdravotni');

  useEffect(() => {
    if (!examinationTypeId) return;
    if (isSudicial) {
      setValue(p('form_data.origin.paid_by_insurance') as any, false);
    } else if (isMedical) {
      setValue(p('form_data.origin.paid_by_insurance') as any, true);
    }
  }, [examinationTypeId, isSudicial, isMedical, setValue]);

  return (
    <Wrapper>
      {/* Základní informace */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Základní informace</SectionLabel>
          </Col>
          <Col md={9}>
            <FieldRow>
              <FieldLabel>Oddělení *</FieldLabel>
              <Select disabled={loading} {...register(p('requesting_department') as any)}>
                <option value="">{loading ? 'Načítám...' : 'Zvolte oddělení'}</option>
                {departmentOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FieldRow>
          </Col>
        </Row>
      </Section>

      {/* Důvod transportu */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Důvod transportu</SectionLabel>
          </Col>
          <Col md={9}>
            <Textarea rows={3} {...register(p('form_data.reason.text') as any)} />
          </Col>
        </Row>
      </Section>

      {/* Místo vyzvednutí */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Místo vyzvednutí</SectionLabel>
          </Col>
          <Col md={9}>
            <CheckList>
              <Check>
                <input
                  type="checkbox"
                  {...register(p('form_data.origin.use_provider_department') as any)}
                />
                <span>Použít adresu poskytovatele</span>
              </Check>
            </CheckList>
            {!useProviderDept && (
              <>
                <FieldRow>
                  <FieldLabel>Název</FieldLabel>
                  <Input type="text" {...register(p('form_data.origin.name_override') as any)} />
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Adresa</FieldLabel>
                  <Input type="text" {...register(p('form_data.origin.address_override') as any)} />
                </FieldRow>
              </>
            )}
            <CheckList style={{ marginTop: Spacing(2) }}>
              <Check>
                <input
                  type="checkbox"
                  disabled={isSudicial}
                  {...register(p('form_data.origin.paid_by_insurance') as any)}
                />
                <span>Hrazeno pojišťovnou</span>
              </Check>
            </CheckList>
          </Col>
        </Row>
      </Section>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: ${Spacing(3)} 0;
  border-bottom: 1px solid ${Colors.lightGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
  padding-top: ${Spacing(1)};
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
  margin-bottom: ${Spacing(2)};
`;

const FieldLabel = styled.label`
  color: ${Colors.grey};
  font-size: 0.9em;
`;

const Input = styled.input`
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;

const Select = styled.select`
  height: 34px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
  max-width: 500px;
`;

const Textarea = styled.textarea`
  border: 1px solid ${Colors.lightGrey};
  padding: ${Spacing(2)};
  width: 100%;
  resize: vertical;
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(2)};
  margin-bottom: ${Spacing(2)};
`;

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
  }

  span {
    color: ${Colors.grey};
  }
`;
